import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function CTA() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  return (
    <section id="contact" className="py-16 md:py-32 bg-white relative overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="relative rounded-3xl overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img
              src="/cta1.jpeg"
              alt="Contact ALEBEL Investment"
              className="w-full h-full object-cover object-center"
            />
            <div className={`absolute inset-0 ${isRTL ? 'bg-gradient-to-l' : 'bg-gradient-to-r'} from-navy/95 via-navy/85 to-navy/70`}></div>
          </div>

          {/* Content */}
          <div className="relative py-12 md:py-24 px-4 md:px-8 lg:px-16">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center`}>
              {/* Left Content */}
              <div className={`space-y-6 md:space-y-8 ${isRTL ? 'lg:order-2' : ''}`}>
                <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="w-12 h-px bg-gold"></div>
                  <span className="text-gold text-sm font-medium tracking-widest uppercase">{t('cta.label')}</span>
                </div>

                <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white leading-tight">
                  {t('cta.title1')}
                  <span className="block text-gold">{t('cta.title2')}</span>
                </h2>

                <p className="text-base md:text-xl text-white/80 leading-relaxed max-w-lg">
                  {t('cta.description')}
                </p>

                {/* CTA Buttons */}
                <div className={`flex flex-wrap gap-4 pt-2 md:pt-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Link
                    to="/contact"
                    className={`px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-gold to-gold-dark text-navy font-semibold rounded-lg hover:shadow-lg hover:shadow-gold/20 transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-3 text-sm md:text-base ${isRTL ? 'flex-row-reverse' : ''}`}
                  >
                    <i className="ri-mail-send-line text-lg md:text-xl"></i>
                    {t('cta.contactUs')}
                  </Link>
                  <a
                    href="tel:+97148886288"
                    className={`px-6 md:px-8 py-3 md:py-4 border border-white/30 text-white font-medium rounded-lg hover:bg-white/10 hover:border-gold/50 transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-3 text-sm md:text-base ${isRTL ? 'flex-row-reverse' : ''}`}
                  >
                    <i className="ri-phone-line text-lg md:text-xl"></i>
                    {t('cta.callNow')}
                  </a>
                </div>
              </div>

              {/* Right Content - Contact Cards */}
              <div className={`space-y-4 md:space-y-6 ${isRTL ? 'lg:order-1' : ''}`}>

                {/* WhatsApp Card */}
                <a href="https://wa.me/97154888408" target="_blank" rel="noopener noreferrer" className="group block bg-white/10 backdrop-blur-sm rounded-2xl p-4 md:p-6 border border-white/10 hover:border-gold/30 hover:bg-white/15 transition-all duration-300 cursor-pointer">
                  <div className={`flex items-center justify-between gap-3 md:gap-5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className={`flex items-center gap-3 md:gap-5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className="w-11 h-11 md:w-14 md:h-14 bg-gold rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                        <i className="ri-whatsapp-line text-xl md:text-2xl text-navy"></i>
                      </div>
                      <div className={isRTL ? 'text-right' : ''}>
                        <div className="text-white/60 text-xs md:text-sm mb-1">WhatsApp</div>
                        <div className="text-white text-sm md:text-xl font-medium" dir="ltr">+971 54 888 4088</div>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <img
                        src="/whatsapp-qr-white-transparent.png"
                        alt="WhatsApp QR"
                        className="w-24 h-24 rounded-lg"
                      />
                    </div>
                  </div>
                </a>

                {/* Landline Card */}
                <a href="tel:+97148886288" className="group block bg-white/10 backdrop-blur-sm rounded-2xl p-4 md:p-6 border border-white/10 hover:border-gold/30 hover:bg-white/15 transition-all duration-300 cursor-pointer">
                  <div className={`flex items-center gap-3 md:gap-5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="w-11 h-11 md:w-14 md:h-14 bg-gold rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                      <i className="ri-phone-line text-xl md:text-2xl text-navy"></i>
                    </div>
                    <div className={isRTL ? 'text-right' : ''}>
                      <div className="text-white/60 text-xs md:text-sm mb-1">Landline</div>
                      <div className="text-white text-sm md:text-xl font-medium" dir="ltr">+971 4 888 6288</div>
                    </div>
                    <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-up-line text-gold text-xl md:text-2xl ${isRTL ? 'mr-auto' : 'ml-auto'} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></i>
                  </div>
                </a>

                {/* Email Card */}
                <a href="mailto:info@alebel.com" className="group block bg-white/10 backdrop-blur-sm rounded-2xl p-4 md:p-6 border border-white/10 hover:border-gold/30 hover:bg-white/15 transition-all duration-300 cursor-pointer">
                  <div className={`flex items-center gap-3 md:gap-5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="w-11 h-11 md:w-14 md:h-14 bg-gold rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                      <i className="ri-mail-line text-xl md:text-2xl text-navy"></i>
                    </div>
                    <div className={isRTL ? 'text-right' : ''}>
                      <div className="text-white/60 text-xs md:text-sm mb-1">{t('cta.email')}</div>
                      <div className="text-white text-sm md:text-xl font-medium">info@alebel.com</div>
                    </div>
                    <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-up-line text-gold text-xl md:text-2xl ${isRTL ? 'mr-auto' : 'ml-auto'} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></i>
                  </div>
                </a>

                {/* Website Card */}
                <a href="https://www.alebel.com" target="_blank" rel="noopener noreferrer" className="group block bg-white/10 backdrop-blur-sm rounded-2xl p-4 md:p-6 border border-white/10 hover:border-gold/30 hover:bg-white/15 transition-all duration-300 cursor-pointer">
                  <div className={`flex items-center gap-3 md:gap-5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="w-11 h-11 md:w-14 md:h-14 bg-gold rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                      <i className="ri-global-line text-xl md:text-2xl text-navy"></i>
                    </div>
                    <div className={isRTL ? 'text-right' : ''}>
                      <div className="text-white/60 text-xs md:text-sm mb-1">{t('cta.website')}</div>
                      <div className="text-white text-sm md:text-xl font-medium">www.alebel.com</div>
                    </div>
                    <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-up-line text-gold text-xl md:text-2xl ${isRTL ? 'mr-auto' : 'ml-auto'} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></i>
                  </div>
                </a>

                {/* Location Card */}
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 md:p-6 border border-white/10">
                  <div className={`flex items-center justify-between gap-3 md:gap-5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className={`flex items-center gap-3 md:gap-5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className="w-11 h-11 md:w-14 md:h-14 bg-gold rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-xl md:text-2xl text-navy"></i>
                      </div>
                      <div className={isRTL ? 'text-right' : ''}>
                        <div className="text-white/60 text-xs md:text-sm mb-1">{t('cta.location')}</div>
                        <div className="text-white text-sm md:text-base font-medium">
                          <div>{isRTL ? 'دبي، الإمارات العربية المتحدة' : 'Dubai, United Arab Emirates'}</div>
                          <div className="text-white/60 text-xs mt-1">{isRTL ? 'مكتب رقم 501، الطابق الخامس، مبنى ناسكو، صلاح الدين' : 'Office No 501, 5th Floor, Nasco Building, Salah Al Din'}</div>
                        </div>
                      </div>
                    </div>
                    {/* QR Code */}
                    <div className="flex flex-col items-center flex-shrink-0">
                      <img
  src="/location-qr-white-transparent.png"
  alt="Location QR Code"
  className="w-24 h-24 rounded-lg"
/>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
